import React, { useCallback, useState } from 'react';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import { useToolkitConfig, ToolkitConfigStep, missingConfig } from './toolkit/ToolkitConfig';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Deposit Folio.
//
// Ported from the desktop toolkit's OHIP › Deposit Folio: each row of a deposit folio spreadsheet (NAME,
// TOTAL_PAID_AMT, DEPARTURE, CONFIRMATION_NO, TRUNC_ARRIVAL) becomes a DEPOSIT FOLIO on its Opera Cloud reservation —
// a charge on the chosen transaction code and its payment, on the chosen folio window — through the project's own
// connection (toolkit ledgers.js post-deposit-folio). Skipped when the reservation is missing, cancelled, no-show or
// checked out, or when the same deposit (amount and payment method) is already on its deposit folio.

const COLUMNS = [
    { key: 'CONFIRMATION_NO', label: 'Confirmation' },
    { key: 'NAME', label: 'Guest' },
    { key: 'ARRIVAL', label: 'Arrival' },
    { key: 'DEPARTURE', label: 'Departure' },
    { key: 'TOTAL_PAID_AMT', label: 'Deposit' },
    { key: 'postedAmount', label: 'Posted' },
];
const SEARCH_KEYS = ['CONFIRMATION_NO', 'NAME'];

function ToolkitDepositFolioPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const config = useToolkitConfig(ctx, { paymentMethods: true, guaranteeCodes: true, transactionCodeKind: 'manual' });
    const [reference, setReference] = useState('Deposit Migration');
    const [folioView, setFolioView] = useState('1');
    const missing = missingConfig(config, [
        !config.paymentMethod && 'a payment method', !config.guaranteeCode && 'a guarantee code']);

    const request = useCallback((row) => ({ area: 'ledger', op: 'post-deposit-folio', args: {
        v5Record: sourceRow(row, ['postedAmount']),
        amount: config.convert ? config.convertAmount(row.TOTAL_PAID_AMT) : row.TOTAL_PAID_AMT,
        paymentMethod: config.paymentMethod, currencyCode: config.currencyCode, cashierId: config.cashierId,
        transactionCode: config.transactionCode, guaranteeCode: config.guaranteeCode,
        paymentsPostingReference: reference, folioView,
    } }), [config, reference, folioView]);
    const interpret = useCallback((r, row) => ({ status: r.status, errorMessage: r.errorMessage || null,
        postedAmount: r.postedAmount ? `${parseFloat(r.postedAmount).toFixed(2)} ${r.currencyCode}` : row.postedAmount }), []);

    const batch = useToolkitBatch({ request, interpret, ctx,
        server: { screen: 'deposit-folio', title: 'Deposit Folio', keyOf: (row) => row.CONFIRMATION_NO } });

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Deposit Folio"
                subtitle="Create deposit folios on Opera Cloud reservations from an Opera v5 deposit folio spreadsheet — a charge and its payment per reservation." />

            <ToolkitConfigStep config={config} number={1} ready={ready}>
                <label><span>Payment reference</span><input type="text" value={reference} onChange={(e) => setReference(e.target.value)} style={{ width: 170 }} /></label>
                <label>
                    <span>Folio window</span>
                    <select value={folioView} onChange={(e) => setFolioView(e.target.value)}>
                        {['1', '2', '3', '4', '5', '6', '7', '8'].map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                </label>
            </ToolkitConfigStep>

            <ToolkitFileStep number={2} title="Opera v5 deposit folio spreadsheet" label="Choose the deposit folio (.xlsx)"
                accept=".xlsx" kind="deposit-folios" onParsed={batch.load} disabled={!ready}>
                {!!batch.rows.length && missing.length > 0 && <p className="tkb-hint">Posting needs {missing.join(', ')}.</p>}
            </ToolkitFileStep>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={SEARCH_KEYS} exportName="Deposit_Folio"
                canRun={ready && !missing.length} onClear={() => batch.load([])} />
        </div>
    );
}

export default ToolkitDepositFolioPage;
